import { useMemo, useRef } from 'react';
import { ConnectedUser } from '../../stores/types/connection';

export interface RememberedUsers {
	users: ConnectedUser[];
	present: Set<number>;
}

/**
 * Keeps everyone the roster has ever reported, so a person who has left is
 * still nameable.
 *
 * The health probe answers with who is connected *now*, which is right for
 * counting the room and wrong for reading its history: the moment someone
 * leaves, every past event of theirs loses the only thing that could put a name
 * on it. Worse, it was intermittent - between their leaving and the next poll
 * the stale roster still named them, so the same row read differently depending
 * on when it was looked at.
 */
export default function useRememberedUsers(users: ConnectedUser[]): RememberedUsers {
	const seen = useRef<Map<number, ConnectedUser>>(new Map());

	return useMemo(() => {
		users.forEach((user) => {
			if (user.gr_user) seen.current.set(user.gr_user, user);
		});

		return {
			users: [...seen.current.values()],
			present: new Set(users.map((user) => user.gr_user)),
		};
	}, [users]);
}
